// Added by Sejune Oh on 2019-07-04
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'
import Rootstore from '.'
import axios from 'axios'
import _ from 'lodash'
import Logger from '../helper/Logger'
import { deleteCookie } from '../helper/util'
import { KEEP_ME_LOGGED_IN_COOKIE } from '../helper/define'

type CachedLocale = {
  [locale: string]: any
}

type GatewayInfo = {
  assigned_group_id?: number
  assigned_group_key?: string
}

export default class ProductStore {
  root?: Rootstore

  needSystemAdmin: boolean = false
  useLoginFormTopImage: boolean = false
  userLevelNames: any = null
  seriesTags: any = null
  studyStatus: any = null
  loginBackgroundImage: any
  loginLeftSectionBackgroundImage: string = ''
  loginTopImage: string = ''
  loadingGif: string = ''
  productBI: string = ''
  siteName: string = ''
  copyright: string = ''
  homepage: string = ''
  pagebarTop: string = ''
  pagebarTopCollapsed: string = ''
  medicalDeviceLabel: string = ''
  ceSerialNumber: string = ''
  ceCertifcatedAt: string = ''
  ceCompany: string = ''
  ceAuthorizedRepresentative: string = ''
  defaultDicomScpAeTitle: string = 'AVIEW'
  enableKeepUserLoggedIn: boolean = true
  productKey: string = ''
  gatewayInfo?: GatewayInfo

  cachedLocale: CachedLocale = {}

  constructor(root: Rootstore) {
    this.root = root
    makeAutoObservable(this)
  }

  get baseURL() {
    const url =
      process.env.NODE_ENV === 'production'
        ? window.location.origin
        : process.env.NODE_ENV === 'test'
        ? process.env.REACT_APP_TEST_URL
        : process.env.REACT_APP_BASE_URL
    return url as string
  }

  init = async () => {
    try {
      const {
        setting: { need_system_admin_setup, use_login_form_top_image, enable_keep_user_logged_in, site_name },
        asset: {
          key,
          copyright,
          name,
          homepage,
          resource: {
            user: { user_level_names },
            data_stroage: { series_tags, study_statuses },
            dicom_scp: { default_ae_title },
          },
          medical_device_labeling: {
            label,
            ce: { serial_number, certifcated_at, company, authorized_representative },
          },
        },
        GatewayInfo,
      }: any = await this.getConfig()

      this.needSystemAdmin = need_system_admin_setup
      this.useLoginFormTopImage = use_login_form_top_image
      this.enableKeepUserLoggedIn = enable_keep_user_logged_in
      this.userLevelNames = user_level_names
      this.seriesTags = series_tags
      this.studyStatus = study_statuses
      this.productBI = name || 'AVIEW'
      this.siteName = site_name || ''
      this.homepage = homepage

      this.copyright =
        copyright ||
        process.env.REACT_APP_VER_RSC_LEGAL_COPYRIGHT ||
        `(c) 2012-${new Date().getFullYear()} Coreline Soft. All rights are reserved.`

      // set document title
      document.title = this.siteName ? `${this.productBI} - ${this.siteName}` : this.productBI

      // medical device labeling
      this.medicalDeviceLabel = label
      this.ceSerialNumber = serial_number
      this.ceCertifcatedAt = certifcated_at
      this.ceCompany = company
      this.ceAuthorizedRepresentative = authorized_representative

      if (default_ae_title) this.defaultDicomScpAeTitle = default_ae_title

      this.productKey = key
      this.gatewayInfo = GatewayInfo
    } catch (err) {
      Logger.errLog('Failed to get product config')
      console.log(err)

      alert('Failed in loading configuration.\n - Check resource directory on server.')
    } finally {
      const basePath = `${this.baseURL}/res/product/image`
      const urlImage = (name: string) => `url(${basePath}/${name}?t=${Date.now()})`

      this.loadingGif = `${urlImage('common.loading.gif')}`
      this.loginBackgroundImage = `${urlImage('login.bg.jpg')}`
      this.loginTopImage = `${urlImage('login.form.top.jpg')}`
      this.loginLeftSectionBackgroundImage = `${urlImage('login.form.left.jpg')}`
      this.pagebarTop = `${urlImage('pagebar.top.jpg')} center no-repeat`
      this.pagebarTopCollapsed = `${urlImage('pagebar.top.collapsed.jpg')} center no-repeat`
      Promise.resolve()
    }
  }

  createSysAdmin = () => {
    this.needSystemAdmin = false
  }

  setKeepUserLoggedInStatus = (status: boolean) => {
    this.enableKeepUserLoggedIn = status
    if (!status) deleteCookie(KEEP_ME_LOGGED_IN_COOKIE)
  }

  getServerTime = async () => {
    const {
      data: { current_time },
    } = await axios.get(`${this.baseURL}/api/v1/system/time`)

    return current_time
  }

  initLocaleData = async (localeCode: string) => {
    // Cached locale
    if (this.cachedLocale[localeCode]) return this.cachedLocale[localeCode]

    // 불필요한 request를 막기위해 5초동안 caching한다.
    const tick = (new Date().getTime() / 1000 / 2).toFixed(0)
    const url = this.baseURL + '/api/v1/system/languages'
    const { data } = await axios.get(`${url}/${localeCode}?t=${tick}`)

    let fallbackData
    if (localeCode === 'en-US') {
      fallbackData = data
    } else {
      const { data: fallback } = await axios.get(`${url}/en-US?t=${tick}`)
      fallbackData = fallback
    }

    // Load fonts
    const fonts = data.$meta.fonts
    const reqFonts: Promise<any>[] = []
    const fontFamilies: string[] = []

    fonts.forEach((f: any) => {
      if (!f.path.woff2) return

      // @ts-ignore
      const fontFace = new FontFace(f.familyName, `url(${this.baseURL}/res/locale/${f.path.woff2})`)
      reqFonts.push(fontFace.load())
      fontFamilies.push(f.familyName)
    })

    // Merge fallback i18n(en-US) and current language i18n json
    const merged = _.merge(fallbackData, data)
    merged.$fontFamily = fontFamilies.join(',')
    merged.$fontSize = fonts[0].fontSize

    this.cachedLocale[localeCode] = merged

    try {
      reqFonts.forEach((_reqFont) => {
        _reqFont.then((font) => {
          // @ts-ignore
          document.fonts.add(font)
        })
      })
    } catch (e) {
      alert('Failed to load fonts')
      console.error(e)
    }

    return merged
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  getConfig = () => {
    return new Promise(async (resolve, reject) => {
      try {
        const url = this.baseURL + '/res/product.json'
        const { data } = await axios.get(url, {
          headers: {
            'cache-control': 'no-cache',
          },
        })
        resolve(data)
      } catch (err) {
        reject(err)
      }
    })
  }
}
